// pages/AddEditStaff.js - Add/Edit Staff (Light Theme)
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Save, User, Mail, Phone, Shield,
    Building, UserCog, X,
    Eye, EyeOff, Lock, Trash2,
    Upload, Image as ImageIcon, MapPin, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from 'react-toastify';

const Panel = ({ children, className = "" }) => (
    <div
        className={`rounded-2xl p-4 sm:p-5 transition-all bg-white ${className}`}
        style={{
            border: '1px solid rgba(255,125,56,0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }}
    >
        {children}
    </div>
);

// ── API Configuration ───────────────────────────────────────────────────────

const STAFF_API_BASE_URL = 'https://api.mobilink.tech/api/superAdmin/staff';
const ROLES_API_URL = 'https://api.mobilink.tech/api/superAdmin/roles/getall-activeroles';
const DEPARTMENTS_API_URL = 'https://api.mobilink.tech/api/superAdmin/department/getall-activedepartments';
const AUTH_TOKEN = sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('subAdminToken') || '';

// ── Staff Service (wired to live API) ───────────────────────────────────────
const StaffService = {
    getById: async (id) => {
        const res = await fetch(`${STAFF_API_BASE_URL}/getstaffbyid`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AUTH_TOKEN}`
            },
            body: JSON.stringify({ staffId: id })
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
            throw new Error(data.message || 'Failed to fetch staff details');
        }
        return data.staff;
    },

    create: async (formData) => {
        const fd = new FormData();
        fd.append('name', formData.name);
        fd.append('phone', formData.phone);
        fd.append('email', formData.email);
        fd.append('password', formData.password);
        fd.append('departmentId', formData.departmentId);
        fd.append('roleId', formData.roleId);
        fd.append('address', formData.address);
        if (formData.imageFile) fd.append('image', formData.imageFile);

        const res = await fetch(`${STAFF_API_BASE_URL}/addstaff`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${AUTH_TOKEN}`
            },
            body: fd
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
            throw new Error(data.message || 'Failed to create staff');
        }
        return data;
    },

    update: async (id, formData) => {
        const fd = new FormData();
        fd.append('name', formData.name);
        fd.append('phone', formData.phone);
        fd.append('email', formData.email);
        if (formData.password) fd.append('password', formData.password);
        fd.append('departmentId', formData.departmentId);
        fd.append('roleId', formData.roleId);
        fd.append('address', formData.address);
        if (formData.imageFile) fd.append('image', formData.imageFile);

        const res = await fetch(`${STAFF_API_BASE_URL}/updatestaff/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${AUTH_TOKEN}`
            },
            body: fd
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
            throw new Error(data.message || 'Failed to update staff');
        }
        return data;
    }

};

// ── Roles / Departments Service ─────────────────────────────────────────────
const LookupService = {
    getActiveRoles: async () => {
        const res = await fetch(ROLES_API_URL, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AUTH_TOKEN}`
            }
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
            throw new Error(data.message || 'Failed to fetch roles');
        }
        return data.activeRoles || [];
    },

    getActiveDepartments: async () => {
        const res = await fetch(DEPARTMENTS_API_URL, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AUTH_TOKEN}`
            }
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
            throw new Error(data.message || 'Failed to fetch departments');
        }
        return data.activeDepartments || [];
    }
};

export const StaffForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        departmentId: '',
        roleId: '',
        address: '',
        status: 'active'
    });
    const [password, setPassword] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEdit);
    const [apiError, setApiError] = useState('');
    const [showPw, setShowPw] = useState(false);

    const [roles, setRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [lookupsLoading, setLookupsLoading] = useState(true);
    const [lookupsError, setLookupsError] = useState('');

    // ── Fetch roles + departments for dropdowns ─────────────────────────
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLookupsLoading(true);
            setLookupsError('');
            try {
                const [activeRoles, activeDepartments] = await Promise.all([
                    LookupService.getActiveRoles(),
                    LookupService.getActiveDepartments()
                ]);
                if (cancelled) return;
                setRoles(activeRoles);
                setDepartments(activeDepartments);
            } catch (err) {
                if (!cancelled) setLookupsError(err.message || 'Failed to load roles/departments');
            } finally {
                if (!cancelled) setLookupsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // ── Fetch existing staff (edit mode) ─────────────────────────────────
    useEffect(() => {
        if (!isEdit) return;

        let cancelled = false;
        (async () => {
            setInitialLoading(true);
            setApiError('');
            try {
                const staff = await StaffService.getById(id);
                if (cancelled) return;
                setFormData(prev => ({
                    ...prev,
                    name: staff.name || '',
                    email: staff.email || '',
                    phone: staff.phone || '',
                    departmentId: staff.departmentId || '',
                    roleId: staff.roleId || '',
                    address: staff.address || '',
                    status: staff.status || 'active'
                }));
                if (staff.image) setImagePreview(staff.image);
            } catch (err) {
                if (!cancelled) {
                    setApiError(err.message || 'Failed to load staff details');
                    toast.error(err.message || 'Failed to load staff details');
                }
            } finally {
                if (!cancelled) setInitialLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [id, isEdit]);

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
        if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
        if (!formData.departmentId.trim()) newErrors.departmentId = 'Department is required';
        if (!formData.roleId.trim()) newErrors.roleId = 'Role is required';
        if (!isEdit && !password) newErrors.password = 'Password is required for new staff';
        if (!isEdit && password && password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        if (isEdit && password && password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ── Image handling ───────────────────────────────────────────────────
    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setApiError('');
        try {
            const payload = { ...formData, password, imageFile };
            if (isEdit) {
                await StaffService.update(id, payload);
                toast.success('Staff updated successfully!');
            } else {
                await StaffService.create(payload);
                toast.success('Staff created successfully!');
            }
            navigate('/admin/staff');
        } catch (err) {
            setApiError(err.message || 'Something went wrong. Please try again.');
            toast.error(err.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/admin/staff');
    };

    if (initialLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 size={20} className="animate-spin text-orange-500" />
                    <span className="text-sm">Loading staff details...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 bg-gray-50 min-h-screen p-4 rounded-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div className="flex items-center gap-3">
                    <button onClick={handleCancel}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100">
                        <ArrowLeft size={16} /> Back
                    </button>
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold text-gray-800">
                            {isEdit ? 'Edit Staff' : 'Add New Staff'}
                        </h1>
                        <p className="text-xs sm:text-sm mt-0.5 text-gray-500">
                            {isEdit ? 'Update staff information' : 'Create a new staff account'}
                        </p>
                    </div>
                </div>
            </div>

            {apiError && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                    <AlertCircle size={16} />
                    <span>{apiError}</span>
                </div>
            )}

            {lookupsError && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                    <AlertCircle size={16} />
                    <span>{lookupsError}</span>
                </div>
            )}

            <Panel className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Full Name *</label>
                            <div className="relative">
                                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                                    placeholder="Enter full name" />
                            </div>
                            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Email Address *</label>
                            <div className="relative">
                                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                                    placeholder="Enter email address" />
                            </div>
                            {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Phone Number *</label>
                            <div className="relative">
                                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                                    placeholder="Enter phone number" />
                            </div>
                            {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">
                                Password {isEdit ? '(leave blank to keep current)' : '*'}
                            </label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                                    className="w-full pl-9 pr-9 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                                    placeholder={isEdit ? 'Enter new password (optional)' : 'Enter password'} />
                                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-500">
                                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Department *</label>
                            <div className="relative">
                                <Building size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <select
                                    value={formData.departmentId}
                                    onChange={e => setFormData({ ...formData, departmentId: e.target.value })}
                                    disabled={lookupsLoading}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all appearance-none bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 disabled:opacity-60"
                                >
                                    <option value="" className="bg-white">
                                        {lookupsLoading ? 'Loading departments...' : 'Select department'}
                                    </option>
                                    {departments.map(dept => (
                                        <option key={dept._id} value={dept._id} className="bg-white">
                                            {dept.departmentName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {errors.departmentId && <p className="mt-1 text-xs text-red-500">{errors.departmentId}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Role *</label>
                            <div className="relative">
                                <Shield size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <select
                                    value={formData.roleId}
                                    onChange={e => setFormData({ ...formData, roleId: e.target.value })}
                                    disabled={lookupsLoading}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all appearance-none bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 disabled:opacity-60"
                                >
                                    <option value="" className="bg-white">
                                        {lookupsLoading ? 'Loading roles...' : 'Select role'}
                                    </option>
                                    {roles.map(role => (
                                        <option key={role._id} value={role._id} className="bg-white">
                                            {role.roleName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {errors.roleId && <p className="mt-1 text-xs text-red-500">{errors.roleId}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Address</label>
                            <div className="relative">
                                <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200"
                                    placeholder="Enter address" />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Status</label>
                            <div className="relative">
                                <UserCog size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl transition-all appearance-none bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200">
                                    <option value="active" className="bg-white">Active</option>
                                    <option value="inactive" className="bg-white">Inactive</option>
                                </select>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Profile Image</label>
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center bg-orange-50 border border-orange-200 flex-shrink-0">
                                    {imagePreview ? (
                                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <ImageIcon size={20} className="text-orange-300" />
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="hidden"
                                        id="staff-image-input"
                                    />
                                    <label htmlFor="staff-image-input"
                                        className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-xl cursor-pointer transition-colors bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100">
                                        <Upload size={14} /> {imagePreview ? 'Change Image' : 'Upload Image'}
                                    </label>
                                    {imagePreview && (
                                        <button type="button" onClick={handleRemoveImage}
                                            className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-xl transition-colors bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100">
                                            <X size={14} /> Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 pt-4 border-t border-orange-200">
                        <button type="button" onClick={handleCancel}
                            className="px-5 py-2.5 text-sm font-medium rounded-xl transition-colors text-gray-600 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading}
                            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl transition-all disabled:opacity-70 bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm">
                            {loading
                                ? <><Loader2 size={16} className="animate-spin" /> Saving...</>
                                : <><Save size={16} /> {isEdit ? 'Update Staff' : 'Create Staff'}</>}
                        </button>
                    </div>
                </form>
            </Panel>
        </div>
    );
};

export default StaffForm;